/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import isEmpty from 'lodash.isempty';
import isNil from 'lodash.isnil';
import {
  User as OidcUser,
  UserManager,
  UserManagerSettings,
  WebStorageStateStore
} from 'oidc-client-ts';
import {MFA, MFAClient, MfaContext} from '@ion/applications/src/authentication/MFA';
import {matchesAuthenticatedUser} from '@ion/applications/src/authentication/OidcClaims';
import {resolveScope} from '@ion/applications/src/authentication/OidcScopes';
import {ApplicationKeys} from '@ion/applications/src/types/application';
import {getLocalStorage, setLocalStorage} from '@ion/core/src/capabilities/storage';
import {get} from '@ion/core/src/environment/environment';
import {error} from '@ion/core/src/logger/logger';
import {
  authenticate,
  authenticateMfa,
  currentUser,
  logout,
  queryOpenIdConfigurationForDomain
} from '@ion/core/src/services/Connect';
import {client, url} from '@ion/core/src/services/ConnectUtils';
import {
  getSessionToken,
  getSessionTokenExpiration,
  setSessionToken
} from '@ion/core/src/services/CTCAuthToken';
import {
  authenticate as sdAuthenticate
} from '@ion/core/src/services/SDConnect';
import {GenericObject, User} from '@ion/core/src/types/entities';
import {LoginResult, MfaProvider, OktaFactor} from '@ion/core/src/types/mfa';
import {Service} from '@ion/core/src/types/services';
import {decode, encode} from '@ion/core/src/utils/Cipher';
import {isSame} from '@ion/core/src/utils/DateUtil';
import {safeJSONParse} from '@ion/core/src/utils/JSONUtils';
import {setAuthToken as setGraphQLAuthToken} from '@ion/services/src/graphql/GraphQLConfig';
import {currentUserQuery} from '@ion/services/src/graphql/QueryAdapter';

/**
 * Provides an enumeration of all OIDC Authentication life-cycle states.
 */
export enum OIDCAuthState {
  OIDC_AUTH_DEFAULT = 'OIDC_AUTH_DEFAULT', //Login screen with username
  OIDC_DISCOVERY_START = 'OIDC_DISCOVERY_START', //Enter Username --> Click Next
  OIDC_DISCOVERY_SUCCESS = 'OIDC_DISCOVERY_SUCCESS', //idpUser = true --> Redirect
  OIDC_DISCOVERY_FAILURE = 'OIDC_DISCOVERY_FAILURE', //idpUser = false, Login screen with Password
  LOGIN_START = 'LOGIN_START', //OIDC_DISCOVERY_SUCCESS ? oktaLogin && parseToken : ctcLogin
  LOGIN_SUCCESS = 'LOGIN_SUCCESS', //parseToken successful || ctcLogin successful
  LOGIN_FAILURE = 'LOGIN_FAILURE', //parseToken fail || ctcLogin fail
  LOGIN_MFA_ENROLL = 'LOGIN_MFA_ENROLL', //202 MFA_ENROLL --> SetupAuthenticator
  LOGIN_AWAITING_MFA = 'LOGIN_AWAITING_MFA', //202 MFA_REQUIRED or activation --> VerifyCode
  PREAUTH_FAILURE = 'PREAUTH_FAILURE' //preuath fail
};

/**
 * Provides an enumeration of various keys related to Login state in persistent storage.
 */
enum LOGIN_PERSISTENCE {
  LOGIN_SESSION = 'session',
  USER_NAME_VERIFICATION_KEY = 'usernameVerification',
  LOGIN_CREDENTIALS = 'login',
  OIDC_USERNAME = 'oidcUsename'
};

/**
 * Provides an enumeration of HTTP error codes which is used to determine sso strict mode.
 */
enum SSO_HTTP_ERROR {
  HTTP_NOT_FOUND = 404,
  HTTP_METHOD_NOT_ALLOWED = 405
}

/**
 * Interface type for SSO discovery response.
 */
interface SSOConfigResponse {
  config: UserManagerSettings;
  isSso: boolean;
  error: boolean;
};

/**
 * The `AuthenticationManager` provides the single source of access for authentication
 * onto the CTC platform.
 *
 * ```typescript
 *
 *   // add example once implemented ...
 *
 * ```
 *
 */
class AuthenticationManager implements MFAClient {
  private static _singleInstance: AuthenticationManager = null;
  private _authState: OIDCAuthState;
  private _oidcUsername: string;
  private _hasIdProvider: boolean;
  private _callbacks: Function[];
  private _isServiceError: boolean;
  private _isInvalid: boolean;
  private _persistCredentials: boolean;
  private _persisted: GenericObject;
  private _emptyCredentials: GenericObject;
  private _user: GenericObject;
  private _authClient: UserManager;
  private _oidcBaseUrl: string;
  private readonly _mfa: MFA;

  public constructor() {
    this._authState = OIDCAuthState.OIDC_AUTH_DEFAULT;
    this._oidcUsername = '';
    this._hasIdProvider = false;
    this._isServiceError = false;
    this._isInvalid = false;
    this._callbacks = [];
    this._emptyCredentials = {
      defaultAccountId : null,
      rememberMe       : false,
      credentials      : {
        username : '',
        password : ''
      }
    };
    this._persisted = this.getPersisted();
    this._persistCredentials = this._persisted.rememberMe;
    this._user = this._persisted.credentials;
    this._authClient = null;
    this._oidcBaseUrl = null;
    this._mfa = new MFA();
  }

  /**
   *
   * Provides a utility to check if OIDC feature is enabled
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  const oidcEnabled = authMgr.getIsOIDCEnabled();
   *
   * ```
   *
   */
  public getIsOIDCEnabled(): boolean {
    return get('oidcEnabled');
  }

  /**
   *
   * Provides a utility to get OIDC base url
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  const oidcUrl = authMgr.oidcBaseUrl;
   *
   * ```
   *
   */
  public get oidcBaseUrl(): string {
    if (!this._oidcBaseUrl) {
      this._oidcBaseUrl = get('oidcBaseUrl') || get('ctcURL');
    }
    return this._oidcBaseUrl;
  }

  /**
   *
   * Register a callback to get notified about auth state changes
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  useEffect(() => {
   *    authMgr.registerCallback(someFn);
   *    return () => authMgr.unregisterCallback(someFn);
   *  }, []);
   *
   * ```
   *
   */
  public registerCallback(cb: Function): void {
    if (cb) {
      this._callbacks.push(cb);
      setTimeout(() => cb(this.authState), 0);
    }
  }

  /**
   *
   * Unregister a callback to get notified about auth state changes
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  useEffect(() => {
   *    authMgr.registerCallback(someFn);
   *    return () => authMgr.unregisterCallback(someFn);
   *  }, []);
   *
   * ```
   *
   */
  public unregisterCallback(cb: Function): void {
    if (cb) {
      const index = this._callbacks.indexOf(cb);
      if (index > -1) {
        this._callbacks.splice(index, 1);
      }
    }
  }

  /**
   * Retrieve persisted user details from local storage
   */
  private getPersisted() {
    const item = safeJSONParse(
      JSON.stringify(this.loginPersistence(LOGIN_PERSISTENCE.LOGIN_CREDENTIALS) || this._emptyCredentials)
    );
    const {password} = item?.credentials;
    if (password) {
      try {
        item.credentials.password = decode(password);
      } catch (err) {
        error(err.toString());
      }
    }
    return item;
  };

  /**
   * Util to store/retrieve user details to local storage
   */
  private loginPersistence(key: LOGIN_PERSISTENCE, remove: boolean = false, value?: any): any {
    if (Object.values(LOGIN_PERSISTENCE).includes(key)) {
      const store = getLocalStorage(ApplicationKeys.iOnSuite) || {};
      if (remove) {
        delete store[key];
      } else if (isNil(value)) {
        return store[key];
      } else {
        store[key] = value;
      }
      setLocalStorage(ApplicationKeys.iOnSuite, store);
    }
  }

  /**
   * Store user details to local storage
   */
  private persistUser(persist: boolean, username: string, password: string): void {
    if (persist) {
      this._user = {username, password: password ? encode(password) : undefined};
      this._persistCredentials = true;
      this._persisted = {
        credentials : this._user,
        rememberMe  : this._persistCredentials
      };
      this.loginPersistence(LOGIN_PERSISTENCE.LOGIN_CREDENTIALS, false, this._persisted);
    } else {
      this.loginPersistence(LOGIN_PERSISTENCE.LOGIN_CREDENTIALS, true);
    }
    this.loginPersistence(LOGIN_PERSISTENCE.LOGIN_SESSION, false, new Date().getTime());
  }

  /**
   *
   * Getter to retrieve rememberMe settings
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  rememberMe = authMgr.persistCredentials;
   *
   * ```
   *
   */
  public get persistCredentials(): boolean {
    return this._persistCredentials;
  }

  /**
   *
   * Setter to store rememberMe settings
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  authMgr.persistCredentials = rememberMe;
   *
   * ```
   *
   */
  public set persistCredentials(persist: boolean) {
    this._persistCredentials = persist;
  }

  /**
   *
   * Getter to retrieve persisted user
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  user = authMgr.persistedUser;
   *
   * ```
   *
   */
  public get persistedUser(): GenericObject {
    return this._user;
  }

  /**
   *
   * Getter to retrieve oidcUsername
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  oidcUsername = authMgr.oidcUsername;
   *
   * ```
   *
   */
  public get oidcUsername(): string {
    if (!this._oidcUsername) {
      this._oidcUsername = this.loginPersistence(LOGIN_PERSISTENCE.OIDC_USERNAME) || '';
    }
    return this._oidcUsername;
  }

  /**
   *
   * Setter to store oidcUsername
   */
  private set oidcUsername(oidcUsername: string) {
    this._oidcUsername = oidcUsername;
    this.loginPersistence(LOGIN_PERSISTENCE.OIDC_USERNAME, false, oidcUsername);
  }

  /**
   *
   * Util to delete temporary oidcUsername
   */
  private deleteOidcUsername(): void {
    this._oidcUsername = null;
    this.loginPersistence(LOGIN_PERSISTENCE.OIDC_USERNAME, true);
  }

  /**
   *
   * Getter to retrieve hasIdProvider
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  oidcUsername = authMgr.hasIdProvider;
   *
   * ```
   *
   */
  public get hasIdProvider(): boolean {
    return this._hasIdProvider;
  }

  /**
   *
   * Util to set Auth State
   */
  private set authState(authState: OIDCAuthState) {
    this._authState = authState;
    this._callbacks.forEach((cb) => cb(authState));
  }

  /**
   *
   * Util to get Auth State
   */
  private get authState(): OIDCAuthState {
    return this._authState;
  }

  /**
   *
   * Getter to retrieve isInvalid
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  isInvalid = authMgr.isInvalid;
   *
   * ```
   *
   */
  public get isInvalid(): boolean {
    return this._isInvalid;
  }

  /**
   * Provides a utility which determines if the given auth state matches the specified auth
   * state.
   */
  private authStateStatus(state: OIDCAuthState, isEqualTo: boolean = true): boolean {
    return isEqualTo ? this._authState === state : this._authState !== state;
  }

  /**
   * Util to set discovery start state
   */
  private oidcDiscoveryStart(): void {
    this._isServiceError = false;
    this._isInvalid = false;
    this.authState = OIDCAuthState.OIDC_DISCOVERY_START;
  }

  /**
   * Util to set discovery fail state
   */
  private oidcDiscoveryFail(): void {
    this.authState = OIDCAuthState.OIDC_DISCOVERY_FAILURE;
    this._hasIdProvider = false;
  }

  /**
   *
   * Util to set discovery status successful
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  authMgr.oidcDiscoverySuccess(oiidcUsername);
   *
   * ```
   *
   */
  public oidcDiscoverySuccess(oidcUsername: string): void {
    this.authState = OIDCAuthState.OIDC_DISCOVERY_SUCCESS;
    this.oidcUsername = oidcUsername;
    this._hasIdProvider = true;
  }

  /**
   *
   * Util to start OIDC discovery routine
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  authMgr.oidcDiscovery(oidcUsername);
   *
   * ```
   *
   */
  public async oidcDiscovery(username): Promise<boolean> {
    let isIdpUser = false;
    try {
      this.oidcDiscoveryStart();
      const {isSso} = await this.getOidcConfig(username);
      isIdpUser = isSso;
      if (isIdpUser) {
        this.oidcDiscoverySuccess(username);
      } else {
        this.oidcDiscoveryFail();
      }
    } catch (err) {
      error(err);
      this.oidcDiscoveryFail();
    }
    return isIdpUser;
  }

  /**
   *
   * Utility which determines if auth state is OIDCAuthState.OIDC_DISCOVERY_FAILURE
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  authMgr.isOidcDiscoveryFailure();
   *
   * ```
   *
   */
  public isOidcDiscoveryFailure(): boolean {
    return this.authStateStatus(OIDCAuthState.OIDC_DISCOVERY_FAILURE);
  }

  /**
   *
   * Utility which determines if auth state is OIDCAuthState.OIDC_AUTH_DEFAULT
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  authMgr.isOidcDiscoveryStart();
   *
   * ```
   *
   */
  public isOidcDiscoveryStart(): boolean {
    return this.authStateStatus(OIDCAuthState.OIDC_AUTH_DEFAULT);
  }

  /**
   *
   * Utility to set auth state to OIDCAuthState.LOGIN_START
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  authMgr.loginStart(username);
   *
   * ```
   *
   */
  public loginStart(oidcUsername: string): void {
    this.authState = OIDCAuthState.LOGIN_START;
    this.oidcUsername = oidcUsername;
  }

  /**
   *
   * Utility to set auth state to OIDCAuthState.LOGIN_SUCCESS
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  authMgr.loginSuccess(username);
   *
   * ```
   *
   */
  public loginSuccess(oidcUsername: string): void {
    this.authState = OIDCAuthState.LOGIN_SUCCESS;
    this.loginPersistence(LOGIN_PERSISTENCE.USER_NAME_VERIFICATION_KEY, false, oidcUsername);
    this._isServiceError = false;
    this._isInvalid = false;
    this.deleteOidcUsername();
  }

  /**
   *
   * Utility to set auth state to OIDCAuthState.LOGIN_FAILURE
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  authMgr.loginFail();
   *
   * ```
   *
   */
  public loginFail(permissionError: boolean = false): void {
    this._isServiceError = true;
    this._isInvalid = !permissionError;
    // there is no standards-based way to close a remote identity provider session,
    // so a failed login clears the locally held one ...
    this._authClient?.removeUser();
    this.authState = OIDCAuthState.LOGIN_FAILURE;
    this._authClient = null;
    this.deleteOidcUsername();
  }

  /**
   *
   * Utility to determine if app is authenticating
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  authMgr.isAuthenticating();
   *
   * ```
   *
   */
  public isAuthenticating(): boolean {
    return (this.authStateStatus(OIDCAuthState.LOGIN_START) ||
      this.authStateStatus(OIDCAuthState.OIDC_DISCOVERY_START));
  }

  /**
   *
   * Utility to determine if app login failed
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  authMgr.isLoginFailure();
   *
   * ```
   *
   */
  public isLoginFailure(): boolean {
    return this.authStateStatus(OIDCAuthState.LOGIN_FAILURE);
  }

  /**
   *
   * Provides a pre-authentication check which first attempts to retrieve the current
   * user if the user has an session timestamp persisted that is within the same day
   * (CTC timeouts are based on a one hour of no activity interval; however, we retry
   * for the duration of a day as the session may continue to be renewed). If returned
   * successfully confirms that the users current session is still active, otherwise,
   * if the call fails, returns null and the user is redirected to the login page.
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  authMgr.preauth(isFromPrime)
   *
   * ```
   *
   * @param  isFromPrime When true, determines if the users session is current active
   *         and if so, retrieves the current user.
   * @param  useApiTierUser When true, indicates that API-Tier GraphQL Services are
   *         to be used when retrieving the current user, otherwise, when `false`
   *         (default), indicates the the current User is to be retrieved via CTC
   *         Services.
   */
  public async preauth(isFromPrime: boolean, useApiTierUser = false): Promise<User> {
    const session = this.loginPersistence(LOGIN_PERSISTENCE.LOGIN_SESSION);
    try {
      const username = this.loginPersistence(LOGIN_PERSISTENCE.USER_NAME_VERIFICATION_KEY);
      const isIdpUser = username ? await this.oidcDiscovery(username) : false;
      const api = !useApiTierUser ? currentUser : currentUserQuery;
      if (isIdpUser) {
        const user = await this.getOidcUser(username);
        if (user) {
          await this.registerToken(user);
          if (useApiTierUser) {
            setGraphQLAuthToken(user.access_token);
          }
          return await api();
        }
      } else {
        if (isFromPrime || (session && isSame(session, new Date(), 'day'))) {
          if (useApiTierUser) {
            const token = await this.getToken();
            setGraphQLAuthToken(token);
          }
          return await api();
        }
      }
    } catch (err) {
    }
    return null;
  }

  /**
   *
   * CTC authentication routine. Returns the user entity on successful login
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  authMgr.ctcLogin(username, password)
   *
   * ```
   *
   * @param  username The username which is to be authenticated with CTC Services.
   * @param  password the password which is to be authenticated with CTC Services.
   * @param  useCTC When true (default), the user will be authenticated with CTC
   *         Services, otherwise, the user will be authenticated with SD Services.
   * @param  useApiTierUser When true, indicates that API-Tier GraphQL Services are
   *         to be used when retrieving the current user, otherwise, when `false`
   *         (default), indicates the the current User is to be retrieved via CTC
   *         Services.
   */
  // eslint-disable-next-line max-params
  public async ctcLogin(username, password, useCTC = true, useApiTierUser = false): Promise<User> {
    let user = null;
    try {
      this.loginStart(username);
      this.persistUser(this.persistCredentials, username, password);
      const api = !useApiTierUser ? currentUser : currentUserQuery;
      useCTC ? await authenticate(username, password) : await sdAuthenticate(username, password);
      if (useApiTierUser) {
        const token = await this.getToken();
        setGraphQLAuthToken(token);
      }
      user = await api();
      this.loginSuccess(username);
    } catch (e) {
      this.loginFail();
    }
    return user;
  }

  /**
   *
   * MFA-aware CTC login. Issues the structured `authenticateMfa` request and branches
   * on the result: on `authenticated` it sets the session token and completes the CTC
   * session (returning the user for the app's existing completion handoff); on `enroll`
   * or `challenge` it stores the transient MFA context and moves the UI into the
   * corresponding MFA state. It never sets a session token without a valid `authToken`.
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  const {result, user} = await authMgr.login(username, password);
   *
   * ```
   *
   * @param  username The username which is to be authenticated with CTC Services.
   * @param  password The password which is to be authenticated with CTC Services.
   * @param  useApiTierUser When true (default), the current user is retrieved via
   *         API-Tier GraphQL Services once authenticated.
   */
  public async login(
    username: string,
    password: string,
    useApiTierUser = true
  ): Promise<{result: LoginResult; user?: User}> {
    this._mfa.startLoginAttempt();
    this.loginStart(username);
    this.persistUser(this.persistCredentials, username, password);
    let result: LoginResult;
    try {
      result = await authenticateMfa(username, password);
    } catch (e) {
      this.loginFail();
      throw e;
    }
    if (result.status === 'authenticated') {
      const user = await this.completeCtcSession(result.authToken, username, useApiTierUser);
      return {result, user};
    }
    try {
      const phase = this._mfa.beginTransaction(result, username);
      this.authState = phase === 'enroll'
        ? OIDCAuthState.LOGIN_MFA_ENROLL
        : OIDCAuthState.LOGIN_AWAITING_MFA;
    } catch (e) {
      if (this._mfa.isMfaSessionExpired()) {
        this.resetAuthState();
      }
    }
    return {result};
  }

  /**
   *
   * Retrieves the QR setup data for a selected TOTP provider by enrolling the factor,
   * storing the returned `factorId` and rotated intermediate token in the MFA context.
   * Any failure is fatal to the transaction, so the outer auth state is reset to
   * login alongside the propagated error.
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  const oktaFactor = await authMgr.enrollFactor('OKTA');
   *
   * ```
   *
   */
  public async enrollFactor(provider: MfaProvider): Promise<OktaFactor> {
    try {
      return await this._mfa.enrollFactor(provider);
    } catch (e) {
      if (this._mfa.isMfaSessionExpired()) {
        this.resetAuthState();
      }
      throw e;
    }
  }

  /**
   *
   * Transitions the enrollment UI from QR display into TOTP activation, rendering
   * `VerifyCode` in activation mode. The MFA phase remains `activation`, and the
   * outer auth state moves to `LOGIN_AWAITING_MFA` once the transition succeeds.
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  authMgr.beginActivation();
   *
   * ```
   *
   */
  public beginActivation(): void {
    this._mfa.beginActivation();
    if (this._mfa.getMfaContext()) {
      this.authState = OIDCAuthState.LOGIN_AWAITING_MFA;
    }
  }

  /**
   *
   * Activates the pending TOTP factor with the first passcode. Per the platform contract
   * activation never returns an auth token, so on success it clears the MFA context and
   * resolves; the user re-authenticates to obtain a session. A message-gated `403`
   * (invalid code) that returns a rotated intermediate token is re-thrown for inline
   * retry with the replacement token; a `403` with no replacement token, or a `401`
   * (consumed token), ends the transaction with `MfaTransactionTerminalError` without
   * resetting the outer auth state; a `429` (the identity provider has temporarily
   * restricted the user) throws `MfaRateLimitError`, also without resetting it; any
   * other error is fatal and resets it to login.
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  await authMgr.enableFactor(passCode);
   *
   * ```
   *
   */
  public async enableFactor(passCode: string): Promise<void> {
    try {
      await this._mfa.enableFactor(passCode);
    } catch (e) {
      if (this._mfa.isMfaSessionExpired()) {
        this.resetAuthState();
      }
      throw e;
    }
  }

  /**
   *
   * Verifies a returning user's TOTP passcode via `MFA.submitTotp()` and, on success,
   * completes the CTC session through the single `completeCtcSession` owner, returning
   * the hydrated user. A message-gated `403` (invalid code) that returns a rotated
   * intermediate token is re-thrown for inline retry with the replacement token; a
   * previously-consumed passcode ("Each code can only be used once") with a rotated
   * token instead throws `MfaReusedCodeError` for the same inline retry, since the
   * authenticator code shown during enrollment is often still on screen when the user
   * immediately signs back in; a `403` with no replacement token, or a `401` (consumed
   * token), ends the transaction with `MfaTransactionTerminalError` without resetting
   * the outer auth state; a `429` (the identity provider has temporarily restricted the
   * user) throws `MfaRateLimitError`, also without resetting it; a response that arrives
   * after the transaction was cancelled throws `MfaTransactionCancelledError`; other
   * errors are fatal, clear the MFA context, and fall back to login.
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  const user = await authMgr.submitTotp(passCode);
   *
   * ```
   *
   */
  public async submitTotp(passCode: string): Promise<User> {
    try {
      const {authToken, username} = await this._mfa.submitTotp(passCode);
      return await this.completeCtcSession(authToken, username);
    } catch (e) {
      if (this._mfa.isMfaSessionExpired()) {
        this.resetAuthState();
      }
      throw e;
    }
  }

  /**
   *
   * Starts the email-based MFA reset ("set up a new device"), clears the MFA context,
   * and returns the masked email hint when provided. The user completes the reset via
   * the emailed link and re-enrolls on next login.
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  const emailHint = await authMgr.resetMfa();
   *
   * ```
   *
   */
  public async resetMfa(): Promise<string | undefined> {
    return this._mfa.resetMfa();
  }

  /**
   *
   * Utility to check if the UI is in the MFA enrollment state.
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  authMgr.isMfaEnroll();
   *
   * ```
   *
   */
  public isMfaEnroll(): boolean {
    return this.authStateStatus(OIDCAuthState.LOGIN_MFA_ENROLL);
  }

  /**
   *
   * Utility to check if the UI is awaiting a TOTP code (challenge or activation).
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  authMgr.isAwaitingMfa();
   *
   * ```
   *
   */
  public isAwaitingMfa(): boolean {
    return this.authStateStatus(OIDCAuthState.LOGIN_AWAITING_MFA);
  }

  /**
   *
   * Returns the transient in-memory MFA context for the current transaction.
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  const ctx = authMgr.getMfaContext();
   *
   * ```
   *
   */
  public getMfaContext(): MfaContext | null {
    return this._mfa.getMfaContext();
  }

  /**
   *
   * Returns `true` when the last MFA transaction failed because the intermediate or
   * state token expired, was consumed, or was missing.
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  authMgr.isMfaSessionExpired();
   *
   * ```
   *
   */
  public isMfaSessionExpired(): boolean {
    return this._mfa.isMfaSessionExpired();
  }

  /**
   *
   * Clears the transient MFA context and returns the UI to the username/password
   * login screen (used when the user cancels or backs out of MFA).
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  authMgr.cancelMfa();
   *
   * ```
   *
   */
  public cancelMfa(): void {
    this._mfa.cancelMfa();
    this.resetAuthState();
  }

  public clearMfaContext(): void {
    this._mfa.clearMfaContext();
  }

  /**
   * The single CTC session-completion owner. Given a valid auth token it persists the
   * session token, establishes the API-Tier GraphQL token, loads the current user, and
   * sets the successful login auth state, returning the hydrated user. Every path that
   * receives an auth token (standard `login`, MFA `submitTotp`) completes through here so
   * session establishment happens exactly once and identically.
   *
   * @param  authToken The auth token returned by the authenticating endpoint.
   * @param  username The username being authenticated (for login-success persistence).
   * @param  useApiTierUser When true (default), the current user is retrieved via
   *         API-Tier GraphQL Services; otherwise via CTC Services.
   */
  private async completeCtcSession(
    authToken: string,
    username: string,
    useApiTierUser = true
  ): Promise<User> {
    setSessionToken(authToken);
    const api = !useApiTierUser ? currentUser : currentUserQuery;
    if (useApiTierUser) {
      const token = await this.getToken();
      setGraphQLAuthToken(token);
    }
    const user = await api();
    this.loginSuccess(username);
    return user;
  }

  /**
   *
   * OIDC authentication routine. Redirects app to OIDC login page
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  authMgr.oidcLogin(username)
   *
   * ```
   *
   */
  public oidcLogin(username): void {
    this.persistUser(this.persistCredentials, username, null);
    this.oidcRedirect(username);
  }

  /**
   *
   * Clean up session related persistent info
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  authMgr.signoutCleanup()
   *
   * ```
   *
   */
  public signoutCleanup() {
    this.loginPersistence(LOGIN_PERSISTENCE.USER_NAME_VERIFICATION_KEY, true);
    this.loginPersistence(LOGIN_PERSISTENCE.LOGIN_SESSION, true);
    this.deleteOidcUsername();
  }

  /**
   *
   * Return the logged in user's username
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  authMgr.getLoggedInUsername()
   *
   * ```
   *
   */
  public getLoggedInUsername(): string {
    return this.loginPersistence(LOGIN_PERSISTENCE.USER_NAME_VERIFICATION_KEY);
  }

  /**
   *
   * Utility to reset auth state to OIDCAuthState.OIDC_AUTH_DEFAULT
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  authMgr.resetAuthState();
   *
   * ```
   *
   */
  public resetAuthState(): void {
    this.authState = OIDCAuthState.OIDC_AUTH_DEFAULT;
  }

  /**
   *
   * Utility to retrieve open id configuration from platform
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  authMgr.getOidcConfig(username, accountId);
   *
   * ```
   */
  public async getOidcConfig(usernameWithDomain?: string, accountId?: string): Promise<SSOConfigResponse> {
    const ssoResponse: SSOConfigResponse = {
      error  : false,
      isSso  : false,
      config : {} as UserManagerSettings
    };
    try {
      const provider = await queryOpenIdConfigurationForDomain(this.oidcBaseUrl, usernameWithDomain, accountId);
      const {issuer = null, client_id: clientId = null, is_default = true, scope = null} = provider || {};
      if (!isNil(issuer) && !isNil(clientId) && is_default === false) {
        const includeArgs = get('_oidcEnabled') === false && this.getIsOIDCEnabled() === true;
        const redirectUri = includeArgs ? `${window.location.origin}/login${window?.location?.search}` : `${window.location.origin}/login`;
        ssoResponse.isSso = true;
        ssoResponse.config = {
          authority            : issuer,
          client_id            : clientId,
          redirect_uri         : redirectUri,
          response_type        : 'code',
          // the callback is read from `location.hash`, so the authorization response
          // must continue to be returned in the fragment ...
          response_mode        : 'fragment',
          scope                : resolveScope(scope),
          // identity is resolved from the ID token's claims, so no userinfo request
          // is ever issued against the identity provider ...
          loadUserInfo         : false,
          automaticSilentRenew : true
        };
      }
    } catch (err) {
      error(err);
      ssoResponse.error = true;
      const {response} = err || {};
      const {status} = response || {};
      if (this.isStrictSSOError(status) && accountId) {
        ssoResponse.isSso = true;
      }
    }
    return ssoResponse;
  }

  /**
   * The getValidDomainFromUsername utility extracts email domain from username
   *
   */
  private getValidDomainFromUsername(username: string) {
    const domain = username ? username.includes('@') && username.split('@').length === 2 ? username.split('@').pop() : null : null;
    if (domain?.split('.').length <= 1) {
      return null;
    }
    return domain;
  }

  /**
   * Utility to return the OIDC auth client instance after retrieving open id
   * configuration from platform. The browser backed token and state stores are
   * built here rather than at module scope, as they reference `window` on
   * instantiation and this module is imported by server rendered pages.
   *
   */
  private async getAuthClient(username?: string): Promise<UserManager> {
    if (!this._authClient) {
      const domain = this.getValidDomainFromUsername(username);
      if (domain) {
        const {config, error, isSso} = await this.getOidcConfig(username);
        if (!error && !isEmpty(config) && isSso) {
          this._authClient = new UserManager({
            ...config,
            // both stores are set explicitly: the library defaults the user store to
            // `sessionStorage`, which would not survive the full page navigations and
            // additional tabs the applications rely on ...
            userStore  : new WebStorageStateStore({store: window.localStorage}),
            stateStore : new WebStorageStateStore({store: window.localStorage})
          });
        }
      }
    }
    return this._authClient;
  }

  /**
   *
   * Utility to remove the authorization response from the address bar once it has
   * been consumed, as the callback is triggered by the presence of the fragment and
   * a retained fragment would replay the callback against a consumed state entry.
   */
  private clearCallbackHash(): void {
    window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
  }

  /**
   * Entry point function to parse out access token from auth code returned
   * by OIDP
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  authMgr.initializeAuthentication(username);
   *
   * ```
   */
  public async initializeAuthentication(username: string): Promise<any> {
    const result = {
      initialized : false,
      state       : null
    };
    try {
      const authClient: UserManager = await this.getAuthClient(username);
      const user: OidcUser = await authClient?.signinRedirectCallback();
      if (!user) {
        throw ('Invalid token');
      }
      result.state = user.state as string;
      this.clearCallbackHash();
      if (matchesAuthenticatedUser(user.profile, username)) {
        await this.registerToken(user);
        result.initialized = true;
      }
    } catch (err) {
      error(err);
    }
    return result;
  }

  /**
   * Provides the internal accessor for the OIDC session held by the authorization
   * client. This is the only place the provider's session object is read, so that
   * every public API of this framework exposes the token as a plain string and no
   * provider SDK type reaches its consumers.
   *
   * @param  username An optional username against which to initialize the
   *         authorization client.
   * @returns A Promise containing the provider session, otherwise `null` when there
   *          is no OIDC session.
   */
  private async getOidcUser(username?: string): Promise<OidcUser | null> {
    const authClient: UserManager = await this.getAuthClient(username);
    return await authClient?.getUser();
  }

  /**
   * Util function to return access token
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  const accessToken = await authMgr.getAccessToken();
   *
   * ```
   *
   * @param  username An optional username against which to initialize the
   *         authorization client.
   * @returns A Promise containing the OIDC access token as a plain string,
   *          otherwise `undefined` when the session has no OIDC token.
   */
  public async getAccessToken(username?: string): Promise<string | undefined> {
    const user = await this.getOidcUser(username);
    return user?.access_token;
  };

  /**
   * Provides an API which allows for referencing the JWT (accessToken) for the authenticated
   * User. This method is intended to be used by clients who need to provide the authorization
   * token in their request headers.
   *
   * ```typescript
   *   import AuthenticationManager from '@ion/applications/src/authentication';
   *
   *   const token = await AuthenticationManager.getToken();
   *   // set client Authorization header ...
   * ```
   *
   * @param  An optional username against which to initialize the authorization client.
   * @returns A Promise containing the authorization (JWT) token for the given session,
   *          falling back to the CTC session token when the session has no OIDC token.
   */
  public async getToken(username?: string): Promise<string> {
    let accessToken: string | undefined;
    try {
      accessToken = await this.getAccessToken(username);
    } catch (err) {
      // an unavailable authorization client leaves the session without an OIDC
      // token, which the CTC session token below covers ...
      accessToken = undefined;
    }
    return accessToken || getSessionToken();
  }

  /**
   * Provides an API which allows for referencing the JWT (accessToken) expiration
   * for the authenticated User. This method is intended to be used by clients who
   * need to perform certain checks based on the expiry of the token.
   *
   * ```typescript
   *   import AuthenticationManager from '@ion/applications/src/authentication';
   *
   *   const expiration = await AuthenticationManager.getExpiration();
   *   ...
   * ```
   *
   * @param  An optional username against which to initialize the authorization
   *         client.
   * @returns A Promise containing the authorization (JWT) token expiration for
   *          the given session.
   */
  public async getExpiration(username?: string): Promise<number | string> {
    let expiration: number | string | undefined;
    try {
      const user = await this.getOidcUser(username);
      // an OIDC session has never resolved a usable expiration here: the provider was
      // handed the access token as a string rather than as the token object it reads
      // the expiry from, so the value has always reached the caller as `NaN`. It is
      // preserved deliberately, as its consumer schedules the preference auto save
      // against it and a real expiry would enable a scheduler which has never run ...
      expiration = user ? Number.NaN : getSessionTokenExpiration();
    } catch (err) {
      expiration = getSessionTokenExpiration();
    }
    return expiration;
  }

  /**
   * Util function to initiate auth by redirecting to oidp portal
   *
   * @param username the user's username.
   */
  private async oidcRedirect(username) {
    const authClient: UserManager = await this.getAuthClient(username);
    if (!authClient) {
      return null;
    }
    // the username travels as application state rather than as the protocol `state`
    // parameter, so it is held in the state store instead of the authorize URL ...
    return authClient.signinRedirect({
      login_hint : username,
      state      : username
    }).catch((err) => {
      this.loginFail();
      error(err);
    });
  }

  /**
   * Util function to renew access token
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  authMgr.renewToken();
   *
   * ```
   */
  public async renewToken(username?: string) {
    const authClient: UserManager = await this.getAuthClient(username);
    const user = await authClient?.signinSilent();
    await this.registerToken(user);
  }

  /**
   * Util function for oidc logout redirect
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  authMgr.oidcLogout();
   *
   * ```
   */
  public async oidcLogout(username?: string) {
    const authClient = await this.getAuthClient(username);
    try {
      logout();
    } catch (e) {}
    const includeArgs = get('_oidcEnabled') === false && this.getIsOIDCEnabled() === true;
    const postLogoutRedirectUri = includeArgs ? `${window.location.origin}${window?.location?.search}` : window.location.origin;
    const user = await authClient?.getUser();
    await authClient?.signoutRedirect({
      post_logout_redirect_uri : postLogoutRedirectUri,
      id_token_hint            : user?.id_token
    });
  }

  /**
   * The registerAccessToken method registers the OIDC access token with CTC,
   * which sets it back as cookie in the browser.
   *
   */
  private async registerAccessToken(user: OidcUser): Promise<any> {
    const {token_type: tokenType, access_token: token} = user;
    const authorization = `${tokenType} ${token}`;
    const result = await client({
      url     : url('/api/token', Service.Base),
      method  : 'post',
      headers : {
        authorization
      }
    });
    return result?.data;
  }

  /**
   * Provides the internal session registration path, guarding a missing session so
   * that a browser without an OIDC session is a no-op rather than an error. Used by
   * the callers which already hold the provider session object.
   *
   * @param  user The provider session whose access token is to be registered.
   * @returns A Promise resolving `true` when the token was registered, otherwise
   *          `false` when there was no token to register.
   */
  private async registerToken(user: OidcUser | null): Promise<boolean> {
    let registered = false;
    if (user) {
      await this.registerAccessToken(user);

      //TODO - In future, set authorization header for individual clients when those services are ready to
      // accept Authorization headers.
      // const {token_type: tokenType, access_token: token} = user;
      // const authHeader = `${tokenType} ${token}`;
      // setConnectAuthorizationHeader(authHeader);
      // setArcGISAuthorizationHeader(authHeader);
      // setSDConnectAuthorizationHeader(authHeader);
      registered = true;
    }
    return registered;
  }

  /**
   *
   * Registers the current session's OIDC access token with CTC via `/api/token`,
   * which sets it back as a cookie in the browser. The token is retrieved from the
   * authorization client internally, so callers never handle a provider token type.
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  await authMgr.registerSession();
   *
   * ```
   *
   * @param  username An optional username against which to initialize the
   *         authorization client.
   * @returns A Promise resolving `true` when the session was registered, otherwise
   *          `false` when the session has no OIDC token.
   */
  public async registerSession(username?: string): Promise<boolean> {
    const user = await this.getOidcUser(username);
    return await this.registerToken(user);
  }

  /**
   *
   * Utility to set auth state to OIDCAuthState.PREAUTH_FAILURE
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  authMgr.preauthFail();
   *
   * ```
   *
   */
  public preauthFail(): void {
    this._isInvalid = false;
    this._isServiceError = true;
    this.authState = OIDCAuthState.PREAUTH_FAILURE;
    this.deleteOidcUsername();
  }

  /**
   *
   * Utility to determine if app preuth failed
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  authMgr.isPreauthFailure();
   *
   * ```
   *
   */
  public isPreauthFailure(): boolean {
    return this.authStateStatus(OIDCAuthState.PREAUTH_FAILURE);
  }

  /**
   *
   * Utility to determine if error code is strict sso http error code
   *
   * ```typescript
   *
   *  import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
   *
   *  authMgr.isStrictSSOError();
   *
   * ```
   *
   */
  private isStrictSSOError(statusCode: number): boolean {
    const codes = Object.values(SSO_HTTP_ERROR);
    return codes.includes(statusCode);
  }
};

export default new AuthenticationManager();
